/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { AnimateSharedLayout, motion } from "framer-motion";
import { useRecoilValue } from "recoil";
import { InternalUserAtom, UserAtom } from "../../atoms/UserAtom";
import { SettingsTab } from "../../pages/settings";
import { localStorageGetItem } from "../../utils/LocalStorage";

const Container = styled.div`
  width: 100%;
  height: fit-content;
  border-bottom: 1px solid #cccccc;
  background-color: #ffffff;
`;

const Grid = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;

  .team-tab-item {
    padding: 15px;

    font-size: 16px;
    line-height: 100%;
    font-weight: 500;
    color: #555;

    position: relative;

    cursor: pointer;

    transition: opacity 0.32s ease-in-out;

    &.selected {
      opacity: 1;
    }

    .underline {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;

      background: #fb972e;
    }
  }
`;

interface Props {
  selected: SettingsTab;
  handleSelected: (vle: SettingsTab) => void;
  isTag?: boolean;
}

export default function Header(props: Props): JSX.Element {
  const { selected, handleSelected } = props;

  const internalUser = useRecoilValue(InternalUserAtom);

  const isAdminAccount =
    process.browser && localStorage && localStorageGetItem("ADMIN_UID");

  const user = useRecoilValue(UserAtom);

  // I'm able to get into my own account
  const isInternalUser = !!internalUser && user?.phone !== internalUser?.phone;

  let screens = [
    ...(props.isTag
      ? [
          {
            enum: SettingsTab.TAGS,
            label: "Tags",
          },
          {
            enum: SettingsTab.CATEGORIES,
            label: "Categories",
          },
          {
            enum: SettingsTab.LINKS,
            label: "Links",
          },
        ]
      : [
          ...(!isInternalUser
            ? [
                {
                  enum: SettingsTab.BILLING,
                  label: "Billing",
                },
              ]
            : []),
          ...(user && !user.lakeRestricted
            ? [
                {
                  enum: SettingsTab.DNS_RECORDS,
                  label: "DNS Records",
                },
              ]
            : []),
          ...(!isAdminAccount
            ? [
                { enum: SettingsTab.GENERAL, label: "General" },
                { enum: SettingsTab.CONTACTS, label: "Contacts" },
              ]
            : []),
          { enum: SettingsTab.API_KEY, label: "Api Key" },
          { enum: SettingsTab.BLOCKING, label: "Blocking" },
          { enum: SettingsTab.EMAIL, label: "Email" },
          { enum: SettingsTab.INTEGRATIONS, label: "Integrations" },
          { enum: SettingsTab.SECURITY, label: "Security" },
          { enum: SettingsTab.USERS, label: "Users" },
        ]),
  ];

  const integrationsEnumCondition =
    selected === SettingsTab.INTEGRATIONS ||
    selected === SettingsTab.LEARNISTIC ||
    selected === SettingsTab.STRIPE ||
    selected === SettingsTab.THRIVECART;

  return (
    <AnimateSharedLayout>
      <Container>
        <Grid>
          {screens
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((screen) =>
              screen.enum === SettingsTab.INTEGRATIONS ? (
                <div
                  key={screen.label}
                  className={`team-tab-item ${
                    integrationsEnumCondition && "selected"
                  }`}
                  onClick={() => handleSelected(screen.enum)}
                >
                  {screen.label}
                  {integrationsEnumCondition ? (
                    <motion.div className="underline" layoutId="underline" />
                  ) : null}
                </div>
              ) : (
                <div
                  key={screen.label}
                  className={`team-tab-item ${
                    screen.enum === selected && "selected"
                  }`}
                  onClick={() => handleSelected(screen.enum)}
                >
                  {screen.label}
                  {screen.enum === selected ? (
                    <motion.div className="underline" layoutId="underline" />
                  ) : null}
                </div>
              )
            )}
        </Grid>
      </Container>
    </AnimateSharedLayout>
  );
}
